import React, { Component } from 'react'
import { BsJournals } from 'react-icons/bs'
import { AiOutlineSearch, AiOutlineWarning } from 'react-icons/ai'
import ModalOrders from '../../components/ModalComponent'
import { getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import { getIndexElement } from '../../functions/searchInObject'
import { NumberFormat } from '../../functions/generalFunctions'
import { pricesFormat } from '../../functions/pricesFormat.js'
import { buildCartLine } from './buildCartLine'
import '../../css/general-style.css'

/**
 * CatalogueV3 — product browser for Sales Order V3. Rebuilt from scratch
 * (2026-08), replacing CatalogueV2.js (1100+ lines: offline caching, service
 * worker thumbnail cache, per-item class-like objects with attached methods,
 * plus its OWN Altitude markup calculation duplicating what SalesOrderV2.js
 * and the backend's addInformation() each computed differently — 3
 * inconsistent formulas for the same number, confirmed 2026-08-13).
 *
 * This version: RDS-only price from product_pricing (/v3/salesorder/catalog —
 * no MAS90/WS call anywhere), simple paginated grid, product-line filter,
 * search. Deliberately drops the offline/service-worker caching layer for
 * now — flagged to the user as a scope trade-off, easy to add back later if
 * field connectivity turns out to need it.
 *
 * Follows the "Product list card" pattern already documented in this
 * project's CLAUDE.md design system.
 *
 * Tiered customer pricing (2026-08-19): passes the SO's selected customer to
 * /v3/salesorder/catalog, so `row.standardPrice` is already that customer's
 * tier price (level1/2/3, default level 1) — nothing else in this file needs
 * to know about tiers. The toolbar badge just reflects what the backend
 * resolved, for visibility. See customerPriceLevel.ts / salesOrderCatalog.ts.
 */
export default class CatalogueV3 extends Component {
    state = {
        showModal: false,
        loading: false,
        products: [],
        total: 0,
        page: 1,
        pageSize: 30,
        search: '',
        productLines: [],
        selectedLine: '',
        // Resolved price tier for the SO's currently selected customer
        // (2026-08-19, tiered pricing) — comes back on every catalog row
        // (same value for all of them, since it's one customer), kept here
        // just so the toolbar badge doesn't need a separate request.
        priceLevel: 1,
    }

    open = () => {
        this.setState({ showModal: true })
        this.fetchProductLines()
        this.fetchCatalog()
    }

    close = () => {
        this.setState({ showModal: false })
    }

    async fetchProductLines() {
        if (this.state.productLines.length > 0) return
        try {
            const resp = await getInformationWithDataV3('/v3/salesorder/product-lines', {})
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ productLines: resp.data })
            }
        } catch (error) {
            console.error('fetchProductLines failed:', error)
        }
    }

    async fetchCatalog() {
        this.setState({ loading: true })
        try {
            const idCompany = getValueCookie('CompanyId')
            const customerNo = this.props.Padre.state.SalesOrder.Customer.CustomerNo
            const resp = await getInformationWithDataV3('/v3/salesorder/catalog', {
                idCompany,
                customerNo,
                search: this.state.search || undefined,
                productLine: this.state.selectedLine || undefined,
                page: this.state.page,
                pageSize: this.state.pageSize,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ loading: false })
                return
            }
            const products = resp.data || []
            // Same resolved level on every row (one customer) — level 1 is the
            // safe default if the catalog came back empty.
            const priceLevel = products.length > 0 ? Number(products[0].resolvedPriceLevel) || 1 : 1
            this.setState({ loading: false, products, total: resp.total || 0, priceLevel })
        } catch (error) {
            console.error('fetchCatalog failed:', error)
            this.setState({ loading: false })
        }
    }

    cartQuantity(itemCode) {
        const products = this.props.Padre.state.Products
        const index = getIndexElement(products, 'itemCode', itemCode)
        if (index === -1) return 0
        return products[index].Visible === 0 ? 0 : Number(products[index].quantityOrdered)
    }

    // itemCodes with a setQuantity currently in flight — Father.state.Products
    // is read synchronously here but setState() only commits async, so two
    // rapid clicks (or a click racing a scan from SearchUPCV3) on the same
    // item can both read the same stale "not in cart yet" snapshot and both
    // unshift a new line, producing a real duplicate row (client reported
    // exactly this class of bug on order AL64684 — see
    // salesOrderIntegrity.ts). This lock makes the second call for the same
    // itemCode a no-op until the first one's setState has committed.
    _inFlightItemCodes = new Set()

    setQuantity(row, quantity) {
        if (this._inFlightItemCodes.has(row.itemCode)) return
        this._inFlightItemCodes.add(row.itemCode)

        const Father = this.props.Padre
        const products = Father.state.Products
        const index = getIndexElement(products, 'itemCode', row.itemCode)
        const qty = Math.max(0, quantity)

        if (index !== -1) {
            const existing = products[index]
            // removeProduct() never actually removes the row — it just sets
            // Visible=0 and leaves it in the array (SalesOrderV3.js's own
            // design, not something to change here). Re-adding a previously
            // removed line must rebuild it from the CURRENT catalog row, not
            // reuse whatever price/cost/level was left on the old object —
            // otherwise it silently keeps a stale UnitPrice from before the
            // removal (confirmed 2026-08-20: removed a line after manually
            // pricing it to the floor, re-added via the catalog, and the cart
            // kept the old floor price instead of the current catalog price
            // for the customer's actual tier).
            if (existing.Visible === 0) {
                products[index] = buildCartLine(row, qty, Father.state.SalesOrder.Customer.CustomerName)
            } else {
                existing.quantityOrdered = qty
                existing.totalCost = qty * Number(existing.UnitPrice)
                existing.Visible = qty > 0 ? 1 : 0
            }
        } else if (qty > 0) {
            const line = buildCartLine(row, qty, Father.state.SalesOrder.Customer.CustomerName)
            products.unshift(line)
        } else {
            this._inFlightItemCodes.delete(row.itemCode)
            return // nothing to add, quantity is 0 and item isn't in cart yet
        }

        // setState is async — refreshInputs() reads DOM nodes by id for every
        // row, including the one just added/removed, so it MUST run after
        // React has committed the new row count, not right after setState()
        // is called. Doing it inline (no callback) crashes on
        // "Cannot set properties of null" for the not-yet-rendered row.
        Father.setState({ Products: products }, () => {
            this._inFlightItemCodes.delete(row.itemCode)
            Father.calculateTotals()
            Father.refreshInputs()
            this.forceUpdate() // re-render this modal's cards with the new quantity
        })
    }

    render() {
        const { showModal, loading, products, total, page, pageSize, search, productLines, selectedLine, priceLevel } = this.state
        const totalPages = Math.max(1, Math.ceil(total / pageSize))

        return (
            <>
                <button hidden={this.props.hidden} disabled={this.props.disabled} type="button" onClick={this.open}
                    className={'btn ' + (this.props.colorButton || 'blueButton') + ' btn-lg'}>
                    <BsJournals /> {this.props.titleButton || 'Open Catalogue'}
                </button>

                <ModalOrders title="Catalogue" show={showModal} close={this.close}>
                    {/* General indicator (2026-08-19) — so the salesperson always knows
                        which price tier is being applied for this customer before adding
                        anything. Level 1 is the default for a customer with no tier set. */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                        <span style={{
                            background: 'rgba(37,99,235,0.08)', color: '#1e40af', border: '1px solid #BFDBFE',
                            borderRadius: '999px', padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700,
                        }}>Pricing: Level {priceLevel}</span>
                    </div>
                    <div className="card mb-3">
                        <div className="card-body" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div style={{ flex: 1, minWidth: '220px' }}>
                                <div style={{ position: 'relative' }}>
                                    <AiOutlineSearch style={{ position: 'absolute', left: '10px', top: '10px', color: '#94A3B8' }} />
                                    <input type="text" className="form-control" style={{ paddingLeft: '32px' }}
                                        placeholder="Search item code or description..." value={search}
                                        onChange={(e) => this.setState({ search: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') this.setState({ page: 1 }, () => this.fetchCatalog()) }} />
                                </div>
                            </div>
                            <div>
                                <select className="form-select" value={selectedLine}
                                    onChange={(e) => this.setState({ selectedLine: e.target.value, page: 1 }, () => this.fetchCatalog())}>
                                    <option value="">All product lines</option>
                                    {productLines.map((pl) => (
                                        <option key={pl.ProductLine} value={pl.ProductLine}>{pl.ProductLineName}</option>
                                    ))}
                                </select>
                            </div>
                            <button className="btn blueButton btn-lg" onClick={() => this.setState({ page: 1 }, () => this.fetchCatalog())}>Search</button>
                        </div>
                    </div>

                    {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>Loading...</div>}

                    <div className="row g-2">
                        {!loading && products.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>No products found</div>
                        )}
                        {products.map((row) => {
                            const qty = this.cartQuantity(row.itemCode)
                            const hasPrice = row.standardPrice !== null && row.standardPrice !== undefined
                            return (
                                <div key={row.itemCode} className="col-sm-12 col-md-6 col-lg-4">
                                    <div style={{
                                        background: '#ffffff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px',
                                        marginBottom: '10px', display: 'flex', gap: '12px', alignItems: 'center',
                                    }}>
                                        <img src={row.itemURLImage || '/assets/image-not-available.png'} alt={row.itemCode}
                                            style={{ width: '84px', height: '84px', objectFit: 'contain', borderRadius: '8px', background: '#F8FAFC', flexShrink: 0, border: '1px solid #F1F5F9', padding: '4px' }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                                <span style={{ background: 'rgba(37,99,235,0.08)', color: '#1e40af', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>{row.itemCode}</span>
                                                <span style={{ background: 'rgba(16,185,129,0.08)', color: '#065F46', border: '1px solid #A7F3D0', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>On hand: {row.quantity || 0}</span>
                                            </div>
                                            {/* Long description (2026-08-20, client's ask) — from CI_Item, falls back to the short one only when it's not synced yet */}
                                            <p style={{ fontSize: '0.85rem', margin: '0 0 6px', color: '#0F172A' }}>{row.longDescription || row.description}</p>
                                            {hasPrice ? (
                                                <p style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0F172A', margin: '0 0 8px' }}>${NumberFormat(pricesFormat(row.standardPrice))}</p>
                                            ) : (
                                                <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#991B1B', display: 'flex', alignItems: 'center', gap: '4px', margin: '0 0 8px' }}>
                                                    <AiOutlineWarning /> No standard price set
                                                </p>
                                            )}
                                            <div className="input-group input-group-sm" style={{ maxWidth: '160px' }}>
                                                <button className="btn grayButton" type="button" onClick={() => this.setQuantity(row, qty - 1)}>-</button>
                                                <input type="number" min="0" className="form-control text-center" value={qty} disabled={!hasPrice}
                                                    onChange={(e) => this.setQuantity(row, Number(e.target.value))} />
                                                {/* No price configured for this company → can't be added. Forces the
                                                    fix at the source (Price Management) instead of an unpriced sale. */}
                                                <button className="btn blueButton" type="button" disabled={!hasPrice} title={!hasPrice ? 'Configure a standard price in Price Management first' : ''}
                                                    onClick={() => this.setQuantity(row, qty + 1)}>+</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                        <div className="btn-group btn-group-lg">
                            <button type="button" disabled={page <= 1} onClick={() => this.setState({ page: page - 1 }, () => this.fetchCatalog())} className="btn blueButton">Prev</button>
                            <span style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', color: '#64748B' }}>Page {page} of {totalPages}</span>
                            <button type="button" disabled={page >= totalPages} onClick={() => this.setState({ page: page + 1 }, () => this.fetchCatalog())} className="btn blueButton">Next</button>
                        </div>
                    </div>
                </ModalOrders>
            </>
        )
    }
}
